import {
  ApartmentSectionOrderId,
  BUILTIN_STAY_SECTION_IDS,
  BuiltinStaySectionId,
  IBuiltinStaySectionConfig,
  ICustomSection,
  IRoom,
  StaticStaySectionId,
} from '@smarttypes/hotel';
import { defaultTo, get } from 'lodash';

import {
  getBuiltinSectionServiceType,
  parseCustomSectionOrderId,
  resolveSectionsOrder,
} from './apartment-sections-order';
import { alignCustomSectionsForLanguage } from './custom-section-reconciliation';

export interface StayInfoSectionDescriptor {
  key: string;
  isTranslationKey: boolean;
  customTitle?: string;
  customSectionId?: string;
}

const BUILTIN_SECTION_KEYS: Record<BuiltinStaySectionId, string> = {
  parking: 'parking',
  breakfast: 'breakfast',
  cleaning: 'cleaning',
  earlyCheckIn: 'early-check-in',
  lateCheckOut: 'late-check-out',
};

export function isBuiltinSectionActive(room: IRoom, sectionId: BuiltinStaySectionId): boolean {
  const config = get(room, `config.builtinSections.${sectionId}`) as IBuiltinStaySectionConfig | undefined;
  return defaultTo(config?.active, true);
}

export function isStaticSectionActive(room: IRoom, sectionId: StaticStaySectionId): boolean {
  return defaultTo(get(room, `config.staticStaySections.${sectionId}.active`), true);
}

/**
 * Returns true when the section has its own saved description for the language,
 * including an explicitly empty value. Missing keys still fall back to additionalInfo.
 */
export function hasOwnStayAdjustmentDescription(
  room: IRoom,
  lang: string,
  sectionId: 'earlyCheckIn' | 'lateCheckOut',
): boolean {
  const descriptionMap = get(room, `apartmentInfo.stayAdjustments.${sectionId}.description`) as
    | Record<string, string | null>
    | undefined;
  return !!descriptionMap && Object.prototype.hasOwnProperty.call(descriptionMap, lang);
}

/**
 * Returns the guest-facing description for early check-in or late check-out.
 * Prefers the per-section language field and falls back to legacy shared additionalInfo
 * only when that language has never been saved on the section.
 */
export function getStayAdjustmentSectionDescription(
  room: IRoom,
  lang: string,
  sectionId: 'earlyCheckIn' | 'lateCheckOut',
): string | undefined {
  if (hasOwnStayAdjustmentDescription(room, lang, sectionId)) {
    const sectionDescription = get(room, `apartmentInfo.stayAdjustments.${sectionId}.description.${lang}`);
    return typeof sectionDescription === 'string' && sectionDescription ? sectionDescription : undefined;
  }
  const sharedDescription = get(room, `apartmentInfo.stayAdjustments.additionalInfo.${lang}`);
  if (typeof sharedDescription === 'string' && sharedDescription) {
    return sharedDescription;
  }
  return undefined;
}

export function hasBuiltinStayInfoContent(room: IRoom, lang: string, sectionId: BuiltinStaySectionId): boolean {
  switch (sectionId) {
    case 'parking':
      return !!(
        get(room, `apartmentInfo.parking.parkingInfo.${lang}`) ||
        get(room, 'apartmentInfo.parking.parkingPrice')
      );
    case 'breakfast':
      return !!(
        get(room, `apartmentInfo.additionalInfo.breakfast.${lang}`) ||
        get(room, 'apartmentInfo.additionalInfo.breakfastPrice')
      );
    case 'cleaning':
      return !!(
        get(room, `apartmentInfo.additionalInfo.cleaningInfo.${lang}`) ||
        get(room, 'apartmentInfo.cleaningPrice')
      );
    case 'earlyCheckIn':
    case 'lateCheckOut':
      return !!getStayAdjustmentSectionDescription(room, lang, sectionId);
    default:
      return false;
  }
}

export function hasStaticStayInfoContent(room: IRoom, lang: string, sectionId: StaticStaySectionId): boolean {
  if (sectionId === 'amenities') {
    if (room.apartmentInfo?.amenitiesPerLang?.[lang]?.length) {
      return true;
    }
    const amenities = room.apartmentInfo?.amenities ?? [];
    return amenities.length > 0;
  }
  if (sectionId === 'transfer') {
    const name = get(room, `apartmentInfo.pickupService.pickUpServiceName.${lang}`);
    const phone = get(room, 'apartmentInfo.pickupService.pickUpServicePhone');
    return !!(name || phone);
  }
  return false;
}

export function resolveStayInfoSectionFromOrderId(
  room: IRoom,
  lang: string,
  orderId: ApartmentSectionOrderId,
  customSections: ICustomSection[],
): StayInfoSectionDescriptor | null {
  const customSectionId = parseCustomSectionOrderId(orderId);
  if (customSectionId) {
    const section = customSections.find(customSection => customSection.sectionId === customSectionId);
    if (!section?.title || (section.serviceType ?? 'info') !== 'info' || !defaultTo(section.active, true)) {
      return null;
    }
    return {
      key: `custom-${customSectionId}`,
      isTranslationKey: false,
      customTitle: section.title,
      customSectionId,
    };
  }
  if (orderId === 'amenities') {
    if (!isStaticSectionActive(room, 'amenities') || !hasStaticStayInfoContent(room, lang, 'amenities')) {
      return null;
    }
    return { key: 'amenities', isTranslationKey: true };
  }
  if (orderId === 'transfer') {
    if (!isStaticSectionActive(room, 'transfer') || !hasStaticStayInfoContent(room, lang, 'transfer')) {
      return null;
    }
    return { key: 'pickup', isTranslationKey: true };
  }
  if (BUILTIN_STAY_SECTION_IDS.includes(orderId as BuiltinStaySectionId)) {
    const builtinId = orderId as BuiltinStaySectionId;
    if (!isBuiltinSectionActive(room, builtinId)) {
      return null;
    }
    if (getBuiltinSectionServiceType(room, builtinId) !== 'info') {
      return null;
    }
    if (!hasBuiltinStayInfoContent(room, lang, builtinId)) {
      return null;
    }
    return {
      key: BUILTIN_SECTION_KEYS[builtinId],
      isTranslationKey: true,
    };
  }
  return null;
}

export function getAlignedCustomSections(room: IRoom, lang: string): ICustomSection[] {
  const allCustomSections = get(room, 'apartmentInfo.customSections', {}) as Record<string, ICustomSection[]>;
  const allLanguages = Object.keys(allCustomSections);
  const sectionsOrder = get(room, 'config.sectionsOrder');
  return alignCustomSectionsForLanguage(allCustomSections, lang, allLanguages, sectionsOrder);
}

export function collectOrderedStayInfoSections(
  room: IRoom,
  lang: string,
  extendStay = false,
): StayInfoSectionDescriptor[] {
  const customSections = getAlignedCustomSections(room, lang);
  const sectionsOrder = resolveSectionsOrder(room, customSections);
  const sections: StayInfoSectionDescriptor[] = [];
  for (const orderId of sectionsOrder.stay ?? []) {
    const section = resolveStayInfoSectionFromOrderId(room, lang, orderId, customSections);
    if (section) {
      sections.push(section);
    }
  }
  if (extendStay) {
    sections.push({
      key: 'extend-stay',
      isTranslationKey: true,
    });
  }
  return sections;
}

export function prependWifiSectionIfMissing(
  sections: StayInfoSectionDescriptor[],
  room: IRoom,
): StayInfoSectionDescriptor[] {
  if (!room.apartmentInfo?.wifi) {
    return sections;
  }
  if (sections.some(section => section.key === 'wifi')) {
    return sections;
  }
  return [{ key: 'wifi', isTranslationKey: true }, ...sections];
}

export function isGetInSectionVisible(room: IRoom): boolean {
  return !get(room, 'config.apartmentSettings.lackOfKeys', false);
}
